import Exception from "src/utils/exception";
import Constants from "src/utils/constants";
import DBOperationsComb from "src/utils/dbOperationsComb";
import EdgeFunction from "src/utils/edgeFunctions";
import Helper from "src/utils/helper";

// The RPCs below are `returns table (...)`, so PostgREST hands back an array
// of rows. Exactly one row is always returned; unwrap it for the caller.
function firstRow(res) {
  return Array.isArray(res?.data) ? res.data[0] : res?.data;
}

async function getEmailTemplateById(id, offlineFirst = true) {
  try {
    if (!Helper.isStringValueExist(id)) {
      return { error: "Email template ID is required", data: null };
    }
    return await DBOperationsComb.getData(
      Constants.enums.TABLES_NAME_ENUM.EMAIL_TEMPLATES_TABLE,
      id,
      offlineFirst
    );
  } catch (error) {
    Exception.catchException(error);
    return null;
  }
}

async function getEmailTemplates(config) {
  try {
    // Admin dashboard is online-only — skip RxDB and hit Supabase directly so
    // the list always reflects current server state (and so we don't wait on
    // an offline cache that this admin-only user-flow doesn't need).
    return await DBOperationsComb.fetchData(
      {
        tableName: Constants.enums.TABLES_NAME_ENUM.EMAIL_TEMPLATES_TABLE,
        ...config
      },
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return { error };
  }
}

async function getEmailTemplateSendConfigs(emailTemplateId) {
  try {
    if (!Helper.isStringValueExist(emailTemplateId)) {
      return { error: "Email template ID is required", data: null };
    }
    return await DBOperationsComb.fetchData(
      {
        tableName:
          Constants.enums.TABLES_NAME_ENUM.EMAIL_TEMPLATE_SEND_CONFIGS_TABLE,
        fetchDeleted: false,
        fetchAll: true,
        sortField: "created_at",
        sortOrder: 1,
        queryParams: [
          {
            field: "email_template_id",
            value: emailTemplateId,
            opType: Constants.enums.COMPARISON_TYPE.EQ
          }
        ]
      },
      false
    );
  } catch (error) {
    Exception.catchException(error);
    return { error };
  }
}

/**
 * Updates an email template.
 *
 * Distribution groups are standalone, shared records; the template's group ids
 * travel inside `defaultSendingData.to` and are written verbatim.
 *
 * Kept separate from the create function rather than merged into an upsert: a
 * single entry point would treat a missing id as "create", so any bug that
 * dropped the id would silently mint a duplicate template — a save that
 * reports success. Two functions make that a loud error instead.
 *
 *
 * @param {Object} params
 * @param {string} params.id - Template id (required).
 * @param {string} params.name - Template name (required).
 * @param {Object} [params.template] - The { subject, body } jsonb.
 * @param {string} [params.description] - Template description; blank ⇒ NULL.
 * @param {Object} [params.source] - Where variables come from: { type, … }.
 * @param {string} [params.userId] - Editor, stored as updated_by.
 * @param {Array} [params.sendConfigs] - The template's whole set of send
 *   configs; replaces the stored set. Null leaves them alone.
 * @returns {Promise<{data: {email_template_id: string}|null, error: string|null}>}
 */
async function updateEmailTemplateWithDistributionGroup({
  id,
  name,
  template = null,
  description = null,
  source = null,
  userId = null,
  defaultSendingData = null,
  sendConfigs = null
} = {}) {
  try {
    if (!Helper.isStringValueExist(id)) {
      return { error: "Email template ID is required", data: null };
    }
    if (!Helper.isStringValueExist(name)) {
      return { error: "Template name is required", data: null };
    }

    const res = await DBOperationsComb.updateEmailTemplateWithDistributionGroup(
      {
        id,
        name,
        template,
        description: Helper.blankToNull(description),
        source: source ?? null,
        userId: Helper.blankToNull(userId),
        defaultSendingData: defaultSendingData ?? null,
        sendConfigs: sendConfigs ?? null
      }
    );
    if (res?.error) return { error: res.error, data: null };

    const row = firstRow(res);
    return { data: row ?? null, error: null };
  } catch (error) {
    Exception.catchException(error);
    return { error: error.message, data: null };
  }
}

/**
 * Creates an email template.
 *
 * Distribution groups are standalone, shared records; the ids of the groups
 * the author picked travel inside `defaultSendingData.to` as JSON *numbers* —
 * `Helper.convertGroupIdsToEmails` only expands numeric entries, so a
 * stringified id would be handed to the mailer as if it were an address.
 *
 * @param {Object} params
 * @param {string} params.name - Template name (required).
 * @param {Object} [params.template] - The { subject, body } jsonb.
 * @param {string} [params.description] - Template description; blank ⇒ NULL.
 * @param {Object} [params.source] - Where variables come from: { type, … }.
 * @param {string} [params.userId] - Author, stored as created_by/updated_by.
 * @param {Array} [params.sendConfigs] - Send configs to create with the
 *   template.
 * @returns {Promise<{data: {email_template_id: string}|null, error: string|null}>}
 */
async function createEmailTemplateWithDistributionGroup({
  name,
  template = {},
  description = null,
  source = null,
  userId = null,
  defaultSendingData = null,
  sendConfigs = null
} = {}) {
  try {
    if (!Helper.isStringValueExist(name)) {
      return { error: "Template name is required", data: null };
    }

    const res = await DBOperationsComb.createEmailTemplateWithDistributionGroup(
      {
        name,
        template: template ?? {},
        description: Helper.blankToNull(description),
        source: source ?? null,
        // Declared `uuid` on the function. An empty string would be
        // cast-checked before the body ever runs and fail the whole call, so
        // send NULL for "not known" instead.
        userId: Helper.blankToNull(userId),
        // The recipient block as the builder composed it, group ids included.
        defaultSendingData: defaultSendingData ?? null,
        sendConfigs: sendConfigs ?? null
      }
    );
    if (res?.error) return { error: res.error, data: null };

    const row = firstRow(res);
    if (!row?.email_template_id) {
      return { error: "Create returned no template id", data: null };
    }
    return { data: row, error: null };
  } catch (error) {
    Exception.catchException(error);
    return { error: error.message, data: null };
  }
}

/**
 * Soft-deletes an email template.
 *
 * Its distribution groups are left alone — groups are shared between
 * templates, so they and their members outlive any one template.
 *
 * @param {Object} params
 * @param {string} params.id - Template id (required).
 * @param {string} [params.userId] - Actor, recorded as updated_by.
 * @returns {Promise<{data: {email_template_id: string}|null, error: string|null}>}
 */
async function deleteEmailTemplateWithDistributionGroup({ id, userId } = {}) {
  try {
    if (!Helper.isStringValueExist(id)) {
      return { error: "Email template ID is required", data: null };
    }

    const res = await DBOperationsComb.deleteEmailTemplateWithDistributionGroup(
      {
        id,
        userId: Helper.blankToNull(userId)
      }
    );
    if (res?.error) return { error: res.error, data: null };

    const row = firstRow(res);
    return { data: row ?? null, error: null };
  } catch (error) {
    Exception.catchException(error);
    return { error: error.message, data: null };
  }
}

/**
 * Sends an email from a template through the send-email-v2 edge function,
 * which works out the recipients (defaults + matching send configs).
 *
 * @param {Object} params
 * @param {string} params.emailTemplateId - Template id (required).
 * @param {Object} params.data - The email data; send configs match against it.
 * @param {{submitter: string, supervisor: string|null}} params.roleRecipients
 * @param {string} params.subject - Already rendered.
 * @param {string} params.body - Already rendered.
 * @param {Array} [params.attachments]
 */
async function sendTemplatedEmail({
  emailTemplateId,
  data = {},
  roleRecipients = {},
  subject = "",
  body = "",
  attachments = []
} = {}) {
  try {
    if (!Helper.isStringValueExist(emailTemplateId)) {
      return { error: "Email template ID is required", data: null };
    }
    return await EdgeFunction.sendEmailV2({
      emailTemplateId,
      data,
      roleRecipients,
      subject,
      body,
      attachments
    });
  } catch (error) {
    Exception.catchException(error);
    return { error: error.message, data: null };
  }
}

export default {
  getEmailTemplateById,
  getEmailTemplates,
  getEmailTemplateSendConfigs,
  createEmailTemplateWithDistributionGroup,
  updateEmailTemplateWithDistributionGroup,
  deleteEmailTemplateWithDistributionGroup,
  sendTemplatedEmail
};
